import { getDownloadConfig } from "@/js/download/api";
import {
  DOWNLOAD_TRANSPORT_MODE,
  normalizeDownloadTransportMode,
  setDownloadTransportRuntimeDefaultMode
} from "@/js/download/transferPolicy";
import {
  DOWNLOAD_SCHEDULING_MODE,
  normalizeDownloadSchedulingMode,
  setDownloadSchedulingRuntimeDefaultMode
} from "@/js/download/schedulingPolicy";

const DEFAULT_BATCH_REQUEST_MAX_ITEMS = 4;

/**
 * Builds the default in-memory config snapshot.
 * @returns {{transportModeDefault:string,schedulingModeDefault:string,batchRequestMaxItems:number}}
 */
const buildDefaultDownloadConfigSnapshot = () => ({
  transportModeDefault: DOWNLOAD_TRANSPORT_MODE.AUTO,
  schedulingModeDefault: DOWNLOAD_SCHEDULING_MODE.PRIORITY,
  batchRequestMaxItems: DEFAULT_BATCH_REQUEST_MAX_ITEMS
});

let downloadConfigLoaded = false;
let downloadConfigPromise = null;
let downloadBatchRequestMaxItems = DEFAULT_BATCH_REQUEST_MAX_ITEMS;
let downloadConfigSnapshot = buildDefaultDownloadConfigSnapshot();

/**
 * Coerces an input into a positive integer.
 * @param {number|string} value
 * @returns {number|null}
 */
const toPositiveInt = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

/**
 * Applies remote config payload to runtime download defaults.
 * @param {Object} config
 * @returns {void}
 */
const applyDownloadConfig = config => {
  if (!config || typeof config !== "object") {
    return;
  }

  const mode = normalizeDownloadTransportMode(config.download_transport_mode_default);
  const appliedMode = setDownloadTransportRuntimeDefaultMode(mode);
  const schedulingMode = normalizeDownloadSchedulingMode(
    config.download_scheduling_mode_default
  );
  const appliedSchedulingMode = setDownloadSchedulingRuntimeDefaultMode(
    schedulingMode
  );
  const batchRequestMaxItems =
    toPositiveInt(config.download_batch_request_max_items) ||
    DEFAULT_BATCH_REQUEST_MAX_ITEMS;

  downloadBatchRequestMaxItems = batchRequestMaxItems;
  downloadConfigSnapshot = {
    transportModeDefault: appliedMode,
    schedulingModeDefault: appliedSchedulingMode,
    batchRequestMaxItems,
    chunkedMinSize: toPositiveInt(config.download_chunked_min_size),
    batchEnabled:
      typeof config.download_batch_enabled === "boolean"
        ? config.download_batch_enabled
        : null,
    batchMaxFiles: toPositiveInt(config.download_batch_max_files),
    batchMaxTotalSize: toPositiveInt(config.download_batch_max_total_size),
    batchMaxSingleFileSize: toPositiveInt(
      config.download_batch_max_single_file_size
    )
  };
};

// Keep frontend runtime defaults aligned when config fetch fails.
/**
 * Restores download runtime defaults used when config fetch fails.
 * @returns {void}
 */
const resetDownloadConfigDefaults = () => {
  setDownloadTransportRuntimeDefaultMode(DOWNLOAD_TRANSPORT_MODE.AUTO);
  setDownloadSchedulingRuntimeDefaultMode(DOWNLOAD_SCHEDULING_MODE.PRIORITY);
  downloadBatchRequestMaxItems = DEFAULT_BATCH_REQUEST_MAX_ITEMS;
  downloadConfigSnapshot = buildDefaultDownloadConfigSnapshot();
};

/**
 * Ensures download config is loaded once and shared between callers.
 * @returns {Promise<Object>}
 */
const ensureDownloadConfig = () => {
  if (downloadConfigLoaded) {
    return Promise.resolve(downloadConfigSnapshot);
  }
  if (downloadConfigPromise) {
    return downloadConfigPromise;
  }
  downloadConfigPromise = getDownloadConfig()
    .then(config => {
      applyDownloadConfig(config);
      downloadConfigLoaded = true;
      downloadConfigPromise = null;
      return downloadConfigSnapshot;
    })
    .catch(() => {
      resetDownloadConfigDefaults();
      // Leave config as "not loaded" so next call can retry network fetch.
      downloadConfigLoaded = false;
      downloadConfigPromise = null;
      return downloadConfigSnapshot;
    });
  return downloadConfigPromise;
};

/**
 * Returns max number of items allowed for a batch request.
 * @returns {number}
 */
const getDownloadBatchRequestMaxItems = () => downloadBatchRequestMaxItems;

/**
 * Returns a copy of current download config snapshot.
 * @returns {Object}
 */
const getDownloadConfigSnapshot = () => ({ ...downloadConfigSnapshot });

export {
  applyDownloadConfig,
  ensureDownloadConfig,
  getDownloadBatchRequestMaxItems,
  getDownloadConfigSnapshot
};
